import { useLayoutEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CTA, trackAttrs } from '../utils/track'
import { FaArrowRight, FaBookOpen, FaHandshake } from 'react-icons/fa'
import ImageLazy from '../components/ui/ImageLazy/ImageLazy'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'
import HeroArtGrid from '../components/sections/HeroArtGrid/HeroArtGrid'
import { coversFrom } from '../utils/coverUrl'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'

export default function ReviewsPage({ items = [] }: { items?: any[] }) {
  const reviews = items

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      const offset = headerH + navH
      document.documentElement.style.setProperty('--header-nav-height', `${offset}px`)
    }

    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 text-[#232323]">

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-navy py-20 md:py-28">
        <ContentColumn anchor="reviews-hero" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <span className="mb-8 inline-flex w-fit items-center gap-2 font-body text-[15px] text-gray-50/80 leading-[1.58] tracking-[-0.003em]">
              <FaBookOpen className="text-base" aria-hidden="true" />
              {reviews.length} Reviews Published
            </span>

            <h1 className="font-sans text-[42px] sm:text-[48px] md:text-[56px] font-bold text-gray-50 mb-6 leading-[1.3] tracking-[-0.02em]">
              Things I've <InlineHighlight color="red">read</InlineHighlight>
            </h1>

            <p className="text-[21px] leading-[1.58] text-gray-50/85 font-body mb-12 tracking-[-0.003em]">
              Honest reviews of books, tools, and resources I've actually used. The ones that shaped how I think about software, leadership, and the move from senior to staff and principal.
            </p>

            <a
              href="#reviews"
              className="group inline-flex items-center justify-center gap-2 bg-gray-50 text-gray-900 font-heading font-bold px-8 py-4 rounded-smooth hover:bg-gray-50/95 transition-colors text-[17px] tracking-[-0.02em]"
            >
              Browse Reviews
              <FaArrowRight
                className="text-sm transition-transform duration-150 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </div>
          {/* The books themselves — no generated art needed when the covers of
              the four newest reviews say it better. */}
          <HeroArtGrid images={coversFrom(reviews)} />
          </div>
        </ContentColumn>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-border-subtle bg-gray-50">
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-label font-body text-gray-900/60">
              <li>
                <Link
                  to="/"
                  className="transition-colors hover:text-gray-900 focus:text-gray-900 focus:outline-none"
                >
                  Home
                </Link>
              </li>
              <li className="font-heading text-gray-900">/</li>
              <li className="font-heading text-gray-900" aria-current="page">
                Reviews
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Reviews Grid */}
      <section id="reviews" className="py-16 md:py-24">
        <ContentColumn anchor="reviews-list" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {reviews.map((article: any) => {
              const articleUrl = article.slug ? `/reviews/${article.slug}` : '/reviews'

              return (
                <Link
                  key={article.id || article.slug}
                  to={articleUrl}
                  className="group flex h-full flex-col overflow-hidden rounded-smooth-lg border-2 border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)]"
                >
                  {(article.image?.url || article.image?.formats?.small?.url) && (
                    <div className="relative aspect-video flex-shrink-0 overflow-hidden">
                      <ImageLazy
                        src={article.image?.formats?.small?.url || article.image?.url}
                        alt={article.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {new Date().getTime() - new Date(article.publishedAt || article.createdAt).getTime() <
                        1000 * 60 * 60 * 24 * 7 && (
                        <span className="absolute left-2 top-2 rounded-smooth bg-red-600 px-2 py-1 font-heading text-[0.65rem] font-bold uppercase text-white">
                          NEW
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="mb-2 font-body text-xs uppercase tracking-[0.18em] text-gray-900/50">
                      Review
                    </span>
                    <h3 className="mb-3 line-clamp-2 font-heading text-lg font-bold uppercase leading-tight text-gray-900">
                      {article.title}
                    </h3>
                    <div className="mt-auto flex items-center justify-between border-t border-gray-900/10 pt-4">
                      <span className="font-body text-xs uppercase tracking-[0.18em] text-gray-900/50">
                        {new Date(article.publishedAt || article.createdAt).toLocaleDateString('en-US', {
                          timeZone: 'UTC',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <FaArrowRight
                        className="text-sm text-gray-900 transition-transform duration-200 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>

          {reviews.length === 0 && (
            <p className="py-16 text-center font-body text-gray-900/70">
              No reviews published yet.
            </p>
          )}
        </ContentColumn>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-brand-navy py-20 text-white md:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-cyan-500/20 to-teal-500/20" />
          </div>
          <div className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-blue-400/20 to-cyan-500/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-gradient-to-tr from-teal-500/15 to-blue-500/15 blur-3xl" />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-gray-900/30 to-gray-900/80" />
        </div>

        <ContentColumn anchor="reviews-cta" className="relative z-10 mx-auto max-w-5xl px-4 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-smooth border border-gray-50/20 bg-brand-cream/10 px-4 py-1 font-heading text-[0.65rem] uppercase tracking-[0.24em] text-white">
            <FaHandshake className="text-sm" aria-hidden="true" />
            Keep Reading
          </div>
          <h2 className="mb-6 font-sans text-[42px] font-bold leading-[1.3] tracking-[-0.02em] text-white sm:text-[48px] md:text-[56px]">
            Get a new review every <InlineHighlight color="red">Monday</InlineHighlight>
          </h2>
          <p className="mx-auto mb-12 max-w-2xl font-body text-[21px] leading-[1.58] tracking-[-0.003em] text-white/85">
            One book, tool, or idea I've actually used, delivered to senior engineers thinking about architecture, leadership, and the path to staff or principal.
          </p>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to="/newsletter"
              className="group inline-flex items-center justify-center gap-2 rounded-lg bg-brand-cream px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-gray-900 transition-colors hover:bg-brand-cream/95"
            >
              Subscribe to the newsletter
              <FaArrowRight
                className="text-base transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
            <Link
              to="/mentorship"
              {...trackAttrs(CTA.mentorship, 'reviews_page')}
              className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-50/40 bg-transparent px-8 py-4 font-heading text-[17px] font-bold tracking-[-0.02em] text-white transition-colors hover:border-gray-50/60 hover:bg-gray-50/10"
            >
              Explore Mentorship
            </Link>
          </div>
        </ContentColumn>
      </section>
    </div>
  )
}
